import { Upload } from "antd";
import { PlusOutlined, LoadingOutlined } from "@ant-design/icons";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";
import { useState, useRef, useEffect } from "react";
import { uploadToS3 } from "../utils/s3";
import useS3Url from "../hooks/useS3Url";

const UploadProfilePicture = ({ value, onChange, disabled }) => {
  const accessToken = useSelector((state) => state.user.accessToken);

  const [imageURL, setImageURL] = useState(value || ""); // Set initial URL
  // Keep in sync when the parent loads/changes the saved image
  useEffect(() => {
    setImageURL(value || "");
  }, [value]);
  const previewURL = useS3Url(imageURL);
  const [uploading, setUploading] = useState(false);
  const [previewLoaded, setPreviewLoaded] = useState(false);
  useEffect(() => {
    setPreviewLoaded(false);
  }, [previewURL]);
  const requestCountRef = useRef(0); // Ref to track request count
  const resetTimeoutRef = useRef(null); // Ref to hold the reset timeout
  const uploadFile = async (file) => {
    try {
      return await uploadToS3(file, accessToken); // S3 key, stored in the DB
    } catch (error) {
      console.error("Error in uploadFile:", error);
      Swal.fire({
        icon: "error",
        title: "Upload failed",
        text: "File upload failed. Please try again.",
      });
      return null;
    }
  };

  const handleImageChange = async ({ file }) => {
    const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
    if (!isJpgOrPng) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "You can only upload JPG/PNG file!",
      });
      return;
    }
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Image must be smaller than 2MB!",
      });
      return;
    }

    if (requestCountRef.current < 10) {
      requestCountRef.current++;
      setUploading(true);
      const uploadedImageURL = await uploadFile(file.originFileObj || file);
      setUploading(false);
      if (uploadedImageURL) {
        setImageURL(uploadedImageURL);
        onChange(uploadedImageURL); // Change this to the expected format
      }
    } else {
      Swal.fire({
        icon: "warning",
        title: "Request limit reached",
        text: "You can only upload a maximum of 10 images every 40 seconds. Please try again later.",
      });
      return;
    }

    // Reset the count after 40 seconds
    if (!resetTimeoutRef.current) {
      resetTimeoutRef.current = setTimeout(() => {
        requestCountRef.current = 0;
        resetTimeoutRef.current = null; // Clear the timeout ref
      }, 40000); // 40,000 ms = 40 seconds
    }
  };

  const uploadingIndicator = (
    <div style={{ color: "#3371EB" }}>
      <LoadingOutlined spin style={{ fontSize: 22 }} />
      <div style={{ marginTop: 8, fontSize: 12 }}>Uploading...</div>
    </div>
  );

  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Upload</div>
    </div>
  );

  return (
    <Upload
      name="avatar"
      listType="picture-circle"
      className="avatar-uploader"
      showUploadList={false}
      beforeUpload={() => false}
      onChange={handleImageChange}
      disabled={disabled || uploading}
    >
      {uploading ? (
        uploadingIndicator
      ) : imageURL ? (
        <div style={{ position: "relative", width: "101px", height: "101px" }}>
          {!previewLoaded && (
            <div className="absolute inset-0 bg-gray-200 animate-pulse rounded-full" />
          )}
          {previewURL && (
            <img
              src={previewURL}
              alt="avatar"
              onLoad={() => setPreviewLoaded(true)}
              onError={() => setPreviewLoaded(true)}
              style={{
                width: "101px",
                height: "101px",
                objectFit: "cover",
                borderRadius: "50%",
                opacity: previewLoaded ? 1 : 0,
                transition: "opacity 0.3s",
              }}
            />
          )}
        </div>
      ) : (
        uploadButton
      )}
    </Upload>
  );
};

export default UploadProfilePicture;
